import { supabase } from '../lib/supabase';
import { notificationService } from './notificationService';

/**
 * Error-surfacing pattern: see the canonical doc-comment at the top of
 * src/services/clientService.js. Services preserve the real error object on
 * `{ data, error }` (no `{ message }` stripping); page-layer callers branch
 * on `error?.code === 'PGRST301'` for RLS copy and otherwise surface the
 * real `error.message` alongside any friendly intro text.
 */
function surfaceError(scope, error) {
  console.error(`[toolboxTalksService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const toolboxTalksService = {
  /**
   * Create a toolbox talk (with optional file upload) and insert the DB record.
   */
  async createTalk(title, siteId, file, createdBy, companyId) {
    try {
      let filePath = null;

      if (file) {
        const fileName = `${companyId}/${siteId}/${Date.now()}_${file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_')}`;

        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('toolbox-talks')
          ?.upload(fileName, file, { upsert: false });

        if (uploadError) throw uploadError;
        filePath = uploadData?.path;
      }

      const { data, error } = await supabase
        ?.from('toolbox_talks')
        ?.insert({
          company_id: companyId,
          site_id: siteId,
          title,
          file_path: filePath,
          created_by: createdBy,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('createTalk', error) };
    }
  },

  /**
   * Assign a toolbox talk to multiple workers.
   */
  async assignTalkToWorkers(talkId, workerUserIds) {
    try {
      if (!workerUserIds?.length) return { data: [], error: null };

      const rows = workerUserIds?.map((wid) => ({
        talk_id: talkId,
        worker_user_id: wid,
      }));

      const { data, error } = await supabase
        ?.from('toolbox_talk_assignments')
        ?.upsert(rows, { onConflict: 'talk_id,worker_user_id' })
        ?.select();

      if (error) throw error;

      const { data: talk } = await supabase?.from('toolbox_talks')?.select('title')?.eq('id', talkId)?.maybeSingle();
      const title = talk?.title || 'Toolbox Talk';
      for (const wid of workerUserIds) {
        notificationService?.notifyToolboxTalkAssigned(talkId, wid, title);
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('assignTalkToWorkers', error) };
    }
  },

  /**
   * Worker confirms attendance for a toolbox talk.
   */
  async confirmAttendance(talkId, workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('toolbox_talk_confirmations')
        ?.upsert(
          { talk_id: talkId, worker_user_id: workerUserId },
          { onConflict: 'talk_id,worker_user_id' }
        )
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('confirmAttendance', error) };
    }
  },

  // NSherjFh PR 10: default-filters soft-deleted toolbox talks.
  // Pass `{ includeDeleted: true }` from the 'View deleted' tab on
  // toolbox-talks-management. Mirrors PR 2-9 opt-in shape exactly.
  async getTalksForSite(siteId, companyId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('toolbox_talks')
        ?.select('*, creator:created_by(id, full_name)')
        ?.eq('site_id', siteId)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getTalksForSite', error) };
    }
  },

  /**
   * Get all toolbox talks for a company (admin view).
   */
  async getTalksForCompany(companyId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('toolbox_talks')
        ?.select('*, site:site_id(id, site_name), creator:created_by(id, full_name)')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getTalksForCompany', error) };
    }
  },

  /**
   * Get all toolbox talks assigned to a worker.
   *
   * 2-step fetch: assignments, then talks, then sites. Avoids the
   * PostgREST resource-embed (`talk:talk_id(...)`) which intermittently
   * fails with a "schema cache" error when the relationship cache is
   * stale for the worker role.
   */
  async getTalksForWorker(workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('toolbox_talk_assignments')
        ?.select('id, talk_id, worker_user_id, assigned_at')
        ?.eq('worker_user_id', workerUserId)
        ?.order('assigned_at', { ascending: false });

      if (error) throw error;

      const assignments = data || [];

      const talkIds = [...new Set(assignments?.map((a) => a?.talk_id)?.filter(Boolean))];
      let talkMap = {};
      if (talkIds?.length) {
        const { data: talks, error: talksError } = await supabase
          ?.from('toolbox_talks')
          ?.select('id, title, file_path, created_at, site_id')
          ?.in('id', talkIds)
          // NSherjFh PR 10: filter soft-deleted talks from the worker
          // queue. Toolbox uses 2-step fetch (not PostgREST embed) so
          // this is a direct .is() rather than the embed+post-filter
          // pattern PR 3 rams + PR 9 inductions use — but the effect
          // is identical: workers don't see soft-deleted talks.
          // Assignments to soft-deleted talks become "orphaned" in the
          // assignments table (still there for historical fact, but
          // talkMap[a?.talk_id] returns undefined, so the enriched
          // assignment ends up with talk=null and the UI drops it).
          ?.is('deleted_at', null);
        if (talksError) throw talksError;
        (talks || [])?.forEach((t) => { talkMap[t?.id] = t; });
      }

      const siteIds = [...new Set(Object.values(talkMap)?.map((t) => t?.site_id)?.filter(Boolean))];
      let siteMap = {};
      if (siteIds?.length) {
        const { data: sites, error: sitesError } = await supabase
          ?.from('sites')
          ?.select('id, site_name')
          ?.in('id', siteIds);
        if (sitesError) throw sitesError;
        (sites || [])?.forEach((s) => { siteMap[s?.id] = s; });
      }

      const enriched = assignments
        ?.map((a) => {
          const talk = talkMap?.[a?.talk_id] || null;
          return {
            ...a,
            talk: talk
              ? { ...talk, site: siteMap?.[talk?.site_id] || null }
              : null,
          };
        })
        // NSherjFh PR 10: drop assignments whose parent is soft-deleted
        // (talkMap excluded them above, so talk is null). The
        // assignment row stays in the DB as historical fact; we just
        // hide it from the worker queue. Same effect as PR 3 rams /
        // PR 9 inductions embed-and-post-filter.
        ?.filter((a) => a?.talk);

      return { data: enriched, error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getTalksForWorker', error) };
    }
  },

  /**
   * Get confirmation status for a specific worker + talk.
   */
  async getWorkerConfirmationStatus(talkId, workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('toolbox_talk_confirmations')
        ?.select('*')
        ?.eq('talk_id', talkId)
        ?.eq('worker_user_id', workerUserId)
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('getWorkerConfirmationStatus', error) };
    }
  },

  /**
   * Get all confirmations for a toolbox talk.
   */
  async getConfirmations(talkId) {
    try {
      const { data, error } = await supabase
        ?.from('toolbox_talk_confirmations')
        ?.select('*, worker:worker_user_id(id, full_name)')
        ?.eq('talk_id', talkId)
        ?.order('confirmed_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getConfirmations', error) };
    }
  },

  /**
   * Get all assignments + confirmation status for all talks in a company.
   */
  async getAllAssignmentsForCompany(companyId) {
    try {
      const { data: talks, error: talksErr } = await supabase
        ?.from('toolbox_talks')
        ?.select('id, title, site:site_id(id, site_name)')
        ?.eq('company_id', companyId)
        // NSherjFh PR 10: the attendance tracking table on toolbox-
        // talks-management is an active view — assignments to soft-
        // deleted talks are hidden from the live grid (their rows
        // stay in toolbox_talk_assignments and toolbox_talk_
        // confirmations as historical fact, but the parent doc is
        // gated out here so the grid + stats roll-up don't count
        // them). Mirrors PR 3 rams getAllAssignmentsForCompany.
        ?.is('deleted_at', null);

      if (talksErr) throw talksErr;
      if (!talks?.length) return { data: [], error: null };

      const talkIds = talks?.map((t) => t?.id);

      const { data: assignments, error: aErr } = await supabase
        ?.from('toolbox_talk_assignments')
        ?.select('*, worker:worker_user_id(id, full_name)')
        ?.in('talk_id', talkIds);

      if (aErr) throw aErr;

      const { data: confirmations, error: cErr } = await supabase
        ?.from('toolbox_talk_confirmations')
        ?.select('talk_id, worker_user_id, confirmed_at')
        ?.in('talk_id', talkIds);

      if (cErr) throw cErr;

      const confirmMap = {};
      confirmations?.forEach((c) => {
        confirmMap[`${c?.talk_id}_${c?.worker_user_id}`] = c?.confirmed_at;
      });

      const talkMap = {};
      talks?.forEach((t) => { talkMap[t?.id] = t; });

      const merged = assignments?.map((a) => ({
        ...a,
        talk: talkMap?.[a?.talk_id],
        confirmed: !!confirmMap?.[`${a?.talk_id}_${a?.worker_user_id}`],
        confirmed_at: confirmMap?.[`${a?.talk_id}_${a?.worker_user_id}`] || null,
      }));

      return { data: merged || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAllAssignmentsForCompany', error) };
    }
  },

  /**
   * Get workers assigned to a site.
   *
   * Two-step fetch. `worker_site_assignments` stores the worker via
   * `user_id` (not `worker_user_id` as the old embed assumed), so the
   * previous `user_profile:worker_user_id(...)` embed raised a PGRST200
   * schema-cache error and the Assign-to-Workers panel never loaded.
   * Fetch the assignment user_ids first, then look up the profiles —
   * avoids any PostgREST relationship-embed surprise and surfaces real
   * errors. Mirrors ramsService.getWorkersForSite. See [NkPx73Lr].
   */
  async getWorkersForSite(siteId) {
    try {
      const { data: assignments, error: assignError } = await supabase
        ?.from('worker_site_assignments')
        ?.select('user_id')
        ?.eq('site_id', siteId);
      if (assignError) throw assignError;
      const userIds = (assignments || [])?.map((a) => a?.user_id)?.filter(Boolean);
      if (!userIds?.length) return { data: [], error: null };
      const { data: profiles, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name')
        ?.in('id', userIds);
      if (profileError) throw profileError;
      return { data: profiles || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getWorkersForSite', error) };
    }
  },

  /**
   * NSherjFh PR 10 — recoverable soft-delete on the toolbox talk.
   *
   * Built from scratch (no existing delete method or UI). Mirrors
   * PR 3 rams softDeleteRamsDocument + PR 9 softDeleteInduction
   * exactly:
   *   - Stamps deleted_at + deleted_by on the row.
   *   - Leaves the storage file in `toolbox-talks` bucket intact so
   *     restore surfaces both row and file.
   *   - Idempotent via `.is('deleted_at', null)`: a second click is
   *     a no-op and preserves the original deleter attribution.
   *   - Existing toolbox_talk_assignments and toolbox_talk_
   *     confirmations rows are NOT touched — they're historical
   *     compliance audit trail (workers' attendance is a fact
   *     regardless of whether the parent is later deleted). Active
   *     tracking grid filters them out via getAllAssignmentsFor
   *     Company's parent-doc filter; per-talk confirmation history
   *     (getConfirmations, getWorkerConfirmationStatus)
   *     intentionally does NOT filter, so audits stay complete.
   *
   * RLS: covered by the existing toolbox_talks_admin_sm_update
   * policy (USING company_id + admin/SM role; no deleted_at
   * predicate, so UPDATE permission applies to both soft-delete
   * and restore).
   */
  async softDeleteToolboxTalk(talkId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('toolbox_talks')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', talkId)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('softDeleteToolboxTalk', error) };
    }
  },

  /**
   * Inverse of softDeleteToolboxTalk. Existing assignments and
   * confirmations remain unchanged across the soft-delete round
   * trip (per the template/acknowledgement split — they're
   * historical facts), so restore is a single UPDATE on the
   * parent row.
   */
  async restoreToolboxTalk(talkId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('toolbox_talks')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', talkId)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('restoreToolboxTalk', error) };
    }
  },

  /**
   * Get a signed URL for a toolbox talk file.
   *
   * Mirrors ramsService.getSignedUrl from [gwwbt45U]: passes the `download`
   * option so non-renderable MIMEs come back as `Content-Disposition:
   * attachment` and behave like clean downloads instead of half-rendering
   * tabs after the popup-blocker fix in TalkCard.
   */
  async getSignedUrl(filePath, downloadFilename) {
    try {
      if (!filePath) {
        const err = new Error('No file path provided');
        err.code = 'NO_FILE_PATH';
        throw err;
      }
      const options = downloadFilename
        ? { download: downloadFilename }
        : { download: true };
      const { data, error } = await supabase?.storage
        ?.from('toolbox-talks')
        ?.createSignedUrl(filePath, 3600, options);

      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: surfaceError('getSignedUrl', error) };
    }
  },
};
